'use client';

import { requestLogin } from '@/lib/auth/login-dialog';

import contentStyles from './PublicContent.module.css';

import { StudioAction } from '@/components/ui/StudioAction';

import { NextIntlClientProvider, useMessages } from 'next-intl';
import { HomepageStyleFilm } from '@/components/auth/HomepageStyleFilm';
import { HomepageCreativityDemo } from '@/components/auth/HomepageCreativityDemo';
import { buildSeoLandingStartHref } from '@/lib/seo/fashion-template-entry';
import type enMessages from '../../../messages/en.json';

/** Reuse the current launch assets; keep the incoming search intent and attribution intact. */
export function StyleDiscoveryProof({ locale, sourcePath }: { locale: import('@/i18n/locales').Locale; sourcePath: string }) {
  const messages = useMessages() as unknown as typeof enMessages;
  const copy = messages.seoLanding.styleDiscovery;
  // Internal navigation must not add UTMs and overwrite the visitor's acquisition source.
  const startHref = sourcePath === '/ai-lookbook-generator'
    ? buildSeoLandingStartHref('aiLookbookGenerator', sourcePath, locale)
    : `/login?${new URLSearchParams({
      redirect: `/explore?${new URLSearchParams({ lang: locale, source_landing: sourcePath })}`,
      login: '1',
      lang: locale,
      source_landing: sourcePath,
    })}`;

  return (
    <NextIntlClientProvider locale={locale} messages={{ auth: { cinematic: {
      styleStory: messages.auth.cinematic.styleStory,
      creativityDemo: messages.auth.cinematic.creativityDemo,
    } } }}>
      <div data-style-discovery-proof className="my-10 md:my-16">
        <HomepageStyleFilm />
        <div className="mx-auto max-w-[1300px] px-5 py-8 md:px-8">
          <p className="max-w-[780px] text-base leading-7 text-[#526779]">{copy.intro}</p>
          <ol className="mt-6 grid gap-6 md:grid-cols-3">
            {(['discover', 'save', 'reuse'] as const).map((step, index) => (
              <li key={step}>
                <span aria-hidden="true" className="text-sm font-semibold text-[#1686AF]">0{index + 1}</span>
                <h3 className="mt-2 text-lg font-semibold text-[#10232E]">{copy[step].title}</h3>
                <p className="mt-2 text-sm leading-6 text-[#526779]">{copy[step].body}</p>
              </li>
            ))}
          </ol>
          <StudioAction href={startHref} variant="primary" className={`mt-7 ${contentStyles.action}`}>
            {copy.cta}
          </StudioAction>
        </div>
        <HomepageCreativityDemo onExplore={() => requestLogin(startHref)} />
      </div>
    </NextIntlClientProvider>
  );
}
